import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAsync, useToast } from '../../lib/store';
import type { EconomyConfigResponse, EconomyDashboard } from '../../lib/types';
import { ErrorState, Panel } from '../ui';
import { formatNumber } from '../../lib/format';

/**
 * The economy console and dashboard.
 *
 * Every figure comes from `LedgerEntry`, which is why `adjustCurrency()` is the
 * only sanctioned way coins move: a direct balance write anywhere in the
 * codebase would be invisible here and would make this page quietly wrong.
 *
 * The faucet/sink split is the thing to read first. Faucets that are mostly
 * one-time (achievements, badges) mean established players are earning nothing
 * even when the totals look healthy — that was the exact failure the audit
 * found before the rebalance.
 */
export function AdminEconomyTab() {
  return (
    <div className="stack">
      <DashboardPanel />
      <ConfigPanel />
    </div>
  );
}

function DashboardPanel() {
  const state = useAsync<EconomyDashboard>(() => api.get('/admin/economy/dashboard', { days: 30 }));

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 200 }} />;

  const data = state.data;
  const inflation = data.createdPerDay > 0 ? (data.netPerDay / data.createdPerDay) * 100 : 0;

  return (
    <>
      <Panel title="▤ Wealth" hint={`${data.players} players`}>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))', gap: 8 }}>
          <Metric label="Median balance" value={formatNumber(data.medianBalance)} />
          <Metric label="Mean balance" value={formatNumber(data.meanBalance)} />
          <Metric label="p10 (poorest)" value={formatNumber(data.p10Balance)} />
          <Metric label="p90" value={formatNumber(data.p90Balance)} />
          <Metric label="Top 1%" value={formatNumber(data.top1Balance)} />
          <Metric label="New players" value={formatNumber(data.newPlayerMedian)} />
          <Metric label="Under 500 coins" value={`${data.brokePlayers}`} />
        </div>
        {/* Median against mean is the inequality read: a mean far above the
            median means a few hoarders, and the median is the number that
            describes what a real player has. */}
        <p className="tiny muted" style={{ marginTop: 'var(--gap-sm)' }}>
          {data.meanBalance > data.medianBalance * 3
            ? 'Mean is far above median — wealth is concentrated. Judge health by the median.'
            : 'Mean and median are close, so the median describes most players well.'}
        </p>
      </Panel>

      <Panel title="▤ Coin flow" hint={`last ${data.windowDays} days`}>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8 }}>
          <Metric label="Created / day" value={formatNumber(data.createdPerDay)} />
          <Metric label="Destroyed / day" value={formatNumber(data.destroyedPerDay)} />
          <Metric
            label="Net / day"
            value={`${data.netPerDay >= 0 ? '+' : ''}${formatNumber(data.netPerDay)}`}
          />
          <Metric label="Inflation" value={`${inflation.toFixed(0)}%`} />
        </div>

        <hr className="divider" />

        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 'var(--gap)' }}>
          <Flow title="Faucets" rows={data.faucets} total={data.created} />
          <Flow title="Sinks" rows={data.sinks} total={data.destroyed} />
        </div>
      </Panel>
    </>
  );
}

function Flow({
  title,
  rows,
  total,
}: {
  title: string;
  rows: Array<{ reason: string; total: number; perDay: number }>;
  total: number;
}) {
  return (
    <div className="stack-sm">
      <strong className="tiny">{title}</strong>
      {rows.length === 0 && <span className="tiny muted">Nothing yet.</span>}
      {rows.map((row) => (
        <div key={row.reason} className="row row--between tiny">
          <span>{row.reason}</span>
          <span className="muted">
            <span className="mono">{formatNumber(row.total)}</span>{' '}
            {total > 0 ? `(${((row.total / total) * 100).toFixed(0)}%)` : ''}
          </span>
        </div>
      ))}
    </div>
  );
}

function ConfigPanel() {
  const { push } = useToast();
  const state = useAsync<EconomyConfigResponse>(() => api.get('/admin/economy/config'));
  const [saving, setSaving] = useState<string | null>(null);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <div className="pxwell skeleton" style={{ height: 300 }} />;

  const groups = [...new Set(state.data.fields.map((field) => field.group))];

  async function save(key: string, value: string) {
    setSaving(key);
    try {
      const result = await api.patch<{ key: string; value: number }>('/admin/economy/config', {
        key,
        value: Number(value),
      });
      push('success', `${key} = ${result.value}`);
      state.reload();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not save that.');
    } finally {
      setSaving(null);
    }
  }

  return (
    <>
      {groups.map((group) => (
        <Panel key={group} title={`⚙ ${group}`}>
          <div className="stack-sm">
            {state.data!.fields
              .filter((field) => field.group === group)
              .map((field) => (
                <div key={field.key} className="row row--between" style={{ gap: 'var(--gap-sm)' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <strong className="tiny">{field.label}</strong>
                    <div className="tiny muted">{field.help}</div>
                    {field.value !== field.default && (
                      <div className="tiny muted">default {field.default}</div>
                    )}
                  </span>
                  <input
                    // Keyed on the effective value, so a save the server clamped
                    // remounts showing what actually took effect rather than the
                    // rejected input.
                    key={`${field.key}:${field.value}`}
                    className="input"
                    type="number"
                    style={{ width: 110 }}
                    defaultValue={field.value}
                    disabled={saving === field.key}
                    onBlur={(event) => {
                      if (Number(event.target.value) !== field.value) {
                        void save(field.key, event.target.value);
                      }
                    }}
                  />
                </div>
              ))}
          </div>
        </Panel>
      ))}
      <p className="tiny muted">
        Values are clamped on save and the field shows what took effect. After changing anything,
        re-run <code>npm run audit:economy -w server</code> to see the before/after.
      </p>
    </>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="stack-sm" style={{ gap: 0 }}>
      <div className="tiny muted">{label}</div>
      <div className="mono">{value}</div>
    </div>
  );
}
